import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, inject, linkedSignal, untracked } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { createRxMutation, createRxResource, NotificationActions } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';

import { ActionsSubject, Store } from '@ngrx/store';
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, map, of, switchMap } from 'rxjs';
import { AppState } from 'src/app/shared/models';
import { LoadStatus } from '../../shared/models/load-status';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlightService } from 'src/app/blight-v2/services/blight.service';
import { BemApiService } from '../../bem/shared/services/bem-api.service';
import { defaultBlightDayRange } from '../../blight-v2/shared/helpers/blight-helpers';
import { CustomerRightsService } from '../../shared/services/customer-rights.service';
import { DateService } from '../../shared/services/date.service';
import { CropYearService } from '../../shared/services/crop-year.service';
import { observeCustomerSwitches } from '../../shared/utils/customer-switch.utils';
import { SprayApplicationDialogService } from '../../shared/spray-applications/services/spray-application-dialog.service';
import { withCustomerParams } from '../../shared/utils/customer-params.utils';
import { dispatchValidationErrorSnackbars } from '../../shared/utils/error-handling.utils';
import { cropYearChanged, openBlightIrrigation, plantDateUpdated } from '../../store/actions';
import { selectCropYear } from '../../store/selectors';
import { mapVirusMonitorCropFieldAdvice } from '../models/mappers';
import { VirusMonitorCalculationStatus, VirusMonitorRegistration } from '../models/models';
import { VirusMonitorNotificationService } from '../services/virus-monitor-notification.service';
import { VirusMonitorService } from '../services/virus-monitor.service';
import { SprayApplicationService } from '../../shared/spray-applications/services/spray-application.service';
import {
  BaseCropFieldRegistration,
  CropFieldAdvice,
  VirusSprayCategory,
} from '@app/shared/components/disease-overview-table.component/models/models';
import { SprayApplicationHistoryCropField } from '../../shared/spray-applications/models/spray-application.models';

interface VirusMonitorResourceParams {
  customerId: number;
  cropYear: number;
  fromDate?: Date;
  toDate?: Date;
}

interface VirusMonitorState {
  fromDate?: Date;
  toDate?: Date;
  hasVirusMonitorPremium?: boolean;
  moduleActivationStatus: LoadStatus;
  selectedCropFieldId?: number;
}

interface CropFieldDateUpdateRequest {
  cropFieldId: number;
  year: number;
  date: Date;
}

const initialState: VirusMonitorState = {
  hasVirusMonitorPremium: false,
  moduleActivationStatus: 'idle',
  fromDate: undefined,
  toDate: undefined,
  selectedCropFieldId: undefined,
};

export const VirusMonitorSignalStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(() => ({
    cropYear: inject(Store<AppState>).selectSignal(selectCropYear),
  })),
  withResource(
    (store) => {
      const virusMonitorService = inject(VirusMonitorService);
      const sprayApplicationService = inject(SprayApplicationService);
      const bemApiService = inject(BemApiService);
      const appStore = inject(Store<AppState>);
      const customer = appStore.selectSignal(UserSelectors.selectCustomer);

      return {
        virusMonitorRegistrations: createRxResource<VirusMonitorRegistration[], VirusMonitorResourceParams | undefined>(
          {
            params: () =>
              withCustomerParams(customer()?.id, (customerId) => {
                if (!store.hasVirusMonitorPremium() || !store.fromDate() || !store.toDate()) {
                  return undefined;
                }

                // Extend date range by ±1 day for chart graph data continuity
                const fromDate = new Date(store.fromDate()!);
                const toDate = new Date(store.toDate()!);
                fromDate.setDate(fromDate.getDate() - 1);
                toDate.setDate(toDate.getDate() + 1);

                return {
                  customerId,
                  fromDate,
                  toDate,
                  cropYear: store.cropYear(),
                };
              }),
            stream: ({ params }) =>
              virusMonitorService
                .getAdvisories(params.cropYear, params.fromDate, params.toDate, params.customerId)
                .pipe(
                  switchMap((registrations) => {
                    const cropFieldIds = registrations.map((registration) => registration.cropFieldId);
                    if (!cropFieldIds.length) {
                      return of(registrations);
                    }

                    return sprayApplicationService.getSprayApplications(cropFieldIds, 'Virus', params.customerId).pipe(
                      map((sprayApplications) =>
                        applySprayApplicationsToVirusMonitorRegistrations(registrations, sprayApplications),
                      ),
                      catchError(() => of(registrations)),
                    );
                  }),
                ),
            defaultValue: [],
            errorMessage: 'errors.virusMonitor.genericError',
          },
        ),
        selectedCropFieldBemParticipation: createRxResource<{ isParticipating: boolean }, number | undefined>({
          params: () => store.selectedCropFieldId(),
          stream: ({ params }) => bemApiService.getIsBemCropField(params),
          defaultValue: { isParticipating: false },
          errorMessage: 'errors.virusMonitor.genericError',
        }),
      };
    },
    {
      errorHandling: 'previous value',
    },
  ),
  withMutations((store) => {
    const virusMonitorService = inject(VirusMonitorService);
    const blightService = inject(BlightService);
    const localeService = inject(LocaleService);
    const appStore = inject(Store<AppState>);
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);

    const updateVirusMonitorRegistration = (
      cropFieldId: number,
      updater: (registration: VirusMonitorRegistration) => VirusMonitorRegistration,
    ): void => {
      patchState(store, {
        virusMonitorRegistrationsValue: untracked(() =>
          store
            .virusMonitorRegistrationsValue()
            .map((registration) => (registration.cropFieldId === cropFieldId ? updater(registration) : registration)),
        ),
      });
    };

    const applyCropFieldRegistrationUpdate = (registration: BaseCropFieldRegistration): void => {
      updateVirusMonitorRegistration(registration.cropFieldId, (currentRegistration) => ({
        ...currentRegistration,
        plantDate: registration.plantDate,
        plantDateCreatedAt: registration.plantDateCreatedAt,
        plantDateUpdatedAt: registration.plantDateUpdatedAt,
        emergenceDate: registration.emergenceDate,
        harvestDate: registration.harvestDate,
        isPlantDateConfirmed: registration.isPlantDateConfirmed,
      }));
    };

    const restoreSelectedVirusMonitorData = (): void => {
      const selectedCropFieldId = untracked(() => store.selectedCropFieldId());
      if (!selectedCropFieldId) {
        return;
      }

      updateVirusMonitorRegistration(selectedCropFieldId, (registration) => ({ ...registration }));
    };

    return {
      recalculateVirusMonitor: createRxMutation<number[], void>({
        operation: (cropFieldIds) => virusMonitorService.recalculateAdvisories(cropFieldIds),
        // Keep SignalR as the primary update path, but refetch on success as a fallback.
        onSuccess: () => {
          store._virusMonitorRegistrationsReload();
        },
        notification: { errorMessage: 'errors.virusMonitor.genericError' },
      }),
      updatePlantDate: createRxMutation<CropFieldDateUpdateRequest, BaseCropFieldRegistration>({
        operation: ({ cropFieldId, date }) => blightService.updatePlantDate(cropFieldId, date, customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
          appStore.dispatch(
            NotificationActions.infoSnackbar({
              message: localeService.translate('BFP.Notifications.PlantDateUpdated', {
                plantDate:
                  registration.plantDate?.toLocaleDateString(localeService.getLocale()) ?? 'Failed to parse plant date',
              }),
            }),
          );
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedVirusMonitorData();
        },
        notification: { errorMessage: 'errors.virusMonitor.genericError' },
      }),
      updateEmergenceDate: createRxMutation<CropFieldDateUpdateRequest, BaseCropFieldRegistration>({
        operation: ({ cropFieldId, date }) => blightService.updateEmergenceDate(cropFieldId, date, customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedVirusMonitorData();
        },
        notification: { errorMessage: 'errors.virusMonitor.genericError' },
      }),
      updateHarvestDate: createRxMutation<CropFieldDateUpdateRequest, BaseCropFieldRegistration>({
        operation: ({ cropFieldId, date }) => blightService.updateHarvestDate(cropFieldId, date, customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedVirusMonitorData();
        },
        notification: { errorMessage: 'errors.virusMonitor.genericError' },
      }),
      updateDeadFoliageDate: createRxMutation<{ cropFieldId: number; date: Date }, void>({
        operation: ({ cropFieldId, date }) => virusMonitorService.updateDeadFoliageDate(cropFieldId, date),
        onSuccess: (_, request) => {
          updateVirusMonitorRegistration(request.cropFieldId, (registration) => ({
            ...registration,
            deadFoliageDate: request.date,
          }));
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedVirusMonitorData();
        },
        notification: {
          successMessage: 'virusMonitor.messages.deadFoliageDateUpdatedSuccessfully.description',
          errorMessage: 'errors.virusMonitor.genericError',
        },
      }),
      updateSprayThreshold: createRxMutation<{ cropFieldId: number; sprayThreshold: number }, void>({
        operation: ({ cropFieldId, sprayThreshold }) =>
          virusMonitorService.updateSprayThreshold(cropFieldId, sprayThreshold),
        onSuccess: (_, request) => {
          updateVirusMonitorRegistration(request.cropFieldId, (registration) => ({
            ...registration,
            sprayThreshold: request.sprayThreshold,
            virusMonitorResult: {
              ...registration.virusMonitorResult,
              days: registration.virusMonitorResult.days.map((day) => ({
                ...day,
                sprayThreshold: request.sprayThreshold,
              })),
              today: registration.virusMonitorResult.today
                ? {
                  ...registration.virusMonitorResult.today,
                  sprayThreshold: request.sprayThreshold,
                }
                : undefined,
            },
          }));
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedVirusMonitorData();
        },
        notification: {
          successMessage: 'virusMonitor.messages.sprayThresholdUpdatedSuccessfully.description',
          errorMessage: 'errors.virusMonitor.genericError',
        },
      }),
      updateProtectionTypes: createRxMutation<
        { cropFieldId: number; selectedProtectionTypes: VirusSprayCategory[] },
        void
      >({
        operation: ({ cropFieldId, selectedProtectionTypes }) =>
          virusMonitorService.updateProtectionTypes(cropFieldId, selectedProtectionTypes),
        onSuccess: (_, request) => {
          updateVirusMonitorRegistration(request.cropFieldId, (registration) => ({
            ...registration,
            selectedProtectionTypes: request.selectedProtectionTypes,
          }));
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedVirusMonitorData();
        },
        notification: {
          successMessage: 'virusMonitor.messages.protectionTypesUpdatedSuccessfully.description',
          errorMessage: 'errors.virusMonitor.genericError',
        },
      }),
    };
  }),
  withComputed((store, dateService = inject(DateService), appStore = inject(Store<AppState>)) => {
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);
    const sortedRegistrations = computed(() =>
      [...store.virusMonitorRegistrationsValue()].sort((a, b) => a.cropFieldName.localeCompare(b.cropFieldName)),
    );
    const eligibleVirusMonitorCropFields = computed((): CropFieldAdvice[] =>
      sortedRegistrations()
        .map(mapVirusMonitorCropFieldAdvice)
        .filter(
          (cropField) => cropField.isCropFieldVisible && isCropFieldEligibleForRecalculation(cropField, dateService),
        ),
    );
    const sortedVirusMonitorAdvices = computed((): CropFieldAdvice[] =>
      store
        .virusMonitorRegistrationsValue()
        .map(mapVirusMonitorCropFieldAdvice)
        .sort((a, b) => a.cropFieldName.localeCompare(b.cropFieldName)),
    );

    return {
      customer,
      hasVirusMonitorRegistrations: computed(() => store.virusMonitorRegistrationsValue().length > 0),
      virusMonitorAdvices: computed((): CropFieldAdvice[] => sortedRegistrations().map(mapVirusMonitorCropFieldAdvice)),
      virusMonitorCalculationDate: linkedSignal<VirusMonitorRegistration[], Date | null>({
        source: () => store.virusMonitorRegistrationsValue(),
        computation: (registrations, previous): Date | null => {
          const newValue = registrations.reduce(
            (minDate, data) => {
              const calculatedOn = data.virusMonitorResult?.calculatedOn ?? null;
              return calculatedOn && (!minDate || calculatedOn < minDate) ? calculatedOn : minDate;
            },
            null as Date | null,
          );
          return newValue ?? previous?.value ?? null;
        },
      }),
      virusMonitorMinAdviceDate: computed((): Date | undefined => {
        const startDates = sortedRegistrations()
          .map((registration) => registration.plantDate)
          .filter((date): date is Date => !!date);

        if (!startDates.length) {
          const cropYear = store.cropYear();
          return cropYear > 0 ? new Date(cropYear, 0, 1) : undefined;
        }

        return startDates.reduce(
          (minDate, current) => new Date(Math.min(minDate.getTime(), current.getTime())),
          startDates[0],
        );
      }),
      virusMonitorMaxAdviceDate: computed((): Date => {
        const endDates = sortedRegistrations()
          .map((registration) => registration.harvestDate)
          .filter((date): date is Date => !!date);

        if (!endDates.length) {
          return dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, store.cropYear());
        }

        return endDates.reduce(
          (maxDate, current) => new Date(Math.max(maxDate.getTime(), current.getTime())),
          endDates[0],
        );
      }),
      eligibleVirusMonitorCropFieldIds: computed((): number[] =>
        eligibleVirusMonitorCropFields().map((cropField) => cropField.cropFieldId),
      ),
      hasEligibleVirusMonitorCropFields: computed((): boolean => eligibleVirusMonitorCropFields().length > 0),
      isVirusMonitorCropFieldsSyncing: computed((): boolean =>
        store
          .virusMonitorRegistrationsValue()
          .some((data) => data.virusMonitorResult.calculationStatus === 'initiated'),
      ),
      selectedCropFieldAdvice: computed((): CropFieldAdvice | undefined =>
        sortedVirusMonitorAdvices().find((a) => a.cropFieldId === store.selectedCropFieldId()),
      ),
      hasSelectedCropFieldAdvices: computed((): boolean => {
        const advice = sortedVirusMonitorAdvices().find((a) => a.cropFieldId === store.selectedCropFieldId());
        return (advice?.advices?.length ?? 0) > 0;
      }),
      isSelectedCropFieldIneligibleForRecalculation: computed((): boolean => {
        const data = store.virusMonitorRegistrationsValue().find((d) => d.cropFieldId === store.selectedCropFieldId());
        if (!data) return false;
        const plantDateInFuture = data.plantDate ? dateService.isInTheFuture(new Date(data.plantDate)) : false;
        const harvestDateInPast = data.harvestDate ? dateService.isInThePast(new Date(data.harvestDate)) : false;
        return plantDateInFuture || harvestDateInPast;
      }),
      selectedVirusMonitorData: computed((): VirusMonitorRegistration | undefined =>
        store.virusMonitorRegistrationsValue().find((d) => d.cropFieldId === store.selectedCropFieldId()),
      ),
      selectedCropFieldIsParticipatingToBem: computed(
        (): boolean => store.selectedCropFieldBemParticipationValue().isParticipating,
      ),
    };
  }),
  withMethods(
    (
      store,
      dateService = inject(DateService),
      appStore = inject(Store<AppState>),
      localeService = inject(LocaleService),
      sprayApplicationDialogService = inject(SprayApplicationDialogService),
    ) => {
      const setCalculatingStatus = (cropFieldId: number, status: VirusMonitorCalculationStatus): void => {
        const currentData = untracked(() => store.virusMonitorRegistrationsValue());
        const updatedData = currentData.map((data) =>
          data.cropFieldId === cropFieldId
            ? {
              ...data,
              virusMonitorResult: {
                ...data.virusMonitorResult,
                calculationStatus: status,
              },
            }
            : data,
        );
        patchState(store, { virusMonitorRegistrationsValue: updatedData });
      };

      return {
        patchSelectedDates(dates: Date[]): void {
          const fromDate = dates[0];
          const toDate = dates.at(-1);

          if (!fromDate || !toDate) {
            return;
          }

          patchState(store, { fromDate, toDate });
        },
        resetVirusMonitorCropfields(): void {
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            store.cropYear(),
          );

          const from = dates[0];
          const to = dates.at(-1)!;

          patchState(store, { fromDate: from, toDate: to });
        },
        resetForCropYear(cropYear: number): void {
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            cropYear,
          );

          patchState(store, {
            fromDate: dates[0],
            toDate: dates.at(-1),
            selectedCropFieldId: undefined,
            virusMonitorRegistrationsValue: [],
            selectedCropFieldBemParticipationValue: { isParticipating: false },
          });
        },
        setCalculatingInitiated(cropFieldId: number): void {
          setCalculatingStatus(cropFieldId, 'initiated');
        },
        notifyCalculationFailed(cropFieldId: number): void {
          setCalculatingStatus(cropFieldId, 'failed');
          appStore.dispatch(
            NotificationActions.errorSnackbar({
              message: localeService.translate('errors.virusMonitor.genericError'),
            }),
          );
        },
        openSpraysDialog(
          sprayType: number,
          advices: CropFieldAdvice[],
          actionDate: Date | undefined,
        ): void {
          sprayApplicationDialogService.openFromCropFields('virus-monitor', advices, sprayType, actionDate);
        },
        openBlightIrrigation(advices: CropFieldAdvice[], actionDate?: Date): void {
          appStore.dispatch(openBlightIrrigation({ advices, actionDate }));
        },
        selectCropField(cropFieldId: number): void {
          patchState(store, { selectedCropFieldId: cropFieldId });
        },
      };
    },
  ),
  withHooks({
    onInit: (store) => {
      const appStore = inject(Store<AppState>);
      const actions = inject(ActionsSubject);
      const customerRightsService = inject(CustomerRightsService);
      const dateService = inject(DateService);
      const cropYearService = inject(CropYearService);
      const notificationService = inject(VirusMonitorNotificationService);

      combineLatest([
        appStore.select(UserSelectors.selectCustomer).pipe(map((customer) => customer?.id), distinctUntilChanged()),
        cropYearService.cropYear$,
      ])
        .pipe(
          distinctUntilChanged(([previousCustomerId, previousCropYear], [customerId, cropYear]) =>
            previousCustomerId === customerId && previousCropYear === cropYear),
          switchMap(([customerId, cropYear]) => {
            patchState(store, {
              hasVirusMonitorPremium: false,
              moduleActivationStatus: customerId ? 'loading' : 'idle',
            });
            return customerId
              ? customerRightsService.getModuleActivation('virus-monitor', cropYear).pipe(
                catchError(() => {
                  patchState(store, { moduleActivationStatus: 'error' });
                  return EMPTY;
                }),
              )
              : EMPTY;
          }),
          takeUntilDestroyed(),
        )
        .subscribe((hasVirusMonitorPremium) => {
          patchState(store, { hasVirusMonitorPremium, moduleActivationStatus: 'loaded' });
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            store.cropYear(),
          );
          store.patchSelectedDates(dates);
        });

      observeCustomerSwitches(appStore)
        .pipe(
          filter(({ previousCustomerId }) => previousCustomerId !== null),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          patchState(store, {
            selectedCropFieldId: undefined,
            virusMonitorRegistrationsValue: [],
            selectedCropFieldBemParticipationValue: { isParticipating: false },
          });
          store._virusMonitorRegistrationsReload();
        });

      notificationService.onInitiated
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.setCalculatingInitiated(event.CropFieldId));

      notificationService.onCompleted.pipe(takeUntilDestroyed()).subscribe(() => store.resetVirusMonitorCropfields());

      actions
        .pipe(
          filter((action) => action.type === cropYearChanged.type),
          map((action) => action as ReturnType<typeof cropYearChanged>),
          takeUntilDestroyed(),
        )
        .subscribe(({ cropYear }) => store.resetForCropYear(cropYear));

      notificationService.onFailed
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.notifyCalculationFailed(event.CropFieldId));

      actions
        .pipe(
          filter((action) => action.type === plantDateUpdated.type),
          takeUntilDestroyed(),
        )
        .subscribe(() => store._virusMonitorRegistrationsReload());
    },
  }),
);

const isCropFieldEligibleForRecalculation = (cropField: CropFieldAdvice, dateService: DateService): boolean =>
  (!cropField.plantDate || !dateService.isInTheFuture(cropField.plantDate)) &&
  (!cropField.harvestDate || !dateService.isInThePast(cropField.harvestDate));

const applySprayApplicationsToVirusMonitorRegistrations = (
  registrations: VirusMonitorRegistration[],
  sprayApplicationsByCropField: SprayApplicationHistoryCropField[],
): VirusMonitorRegistration[] => {
  const sprayApplicationsByDateAndCropField = new Map<number, Map<number, Date>>();

  sprayApplicationsByCropField.forEach((cropField) => {
    sprayApplicationsByDateAndCropField.set(cropField.id, buildLatestSprayApplicationByDay(cropField));
  });

  return registrations.map((registration) => {
    const sprayApplicationsByDay = sprayApplicationsByDateAndCropField.get(registration.cropFieldId);
    if (!sprayApplicationsByDay) {
      return registration;
    }

    return {
      ...registration,
      virusMonitorResult: {
        ...registration.virusMonitorResult,
        days: registration.virusMonitorResult.days.map((day) => ({
          ...day,
          sprayTime: getMostRecentDate(sprayApplicationsByDay.get(getUtcDateKey(day.date)), day.sprayTime),
        })),
        today: registration.virusMonitorResult.today
          ? {
            ...registration.virusMonitorResult.today,
            sprayTime: getMostRecentDate(
              sprayApplicationsByDay.get(getUtcDateKey(registration.virusMonitorResult.today.date)),
              registration.virusMonitorResult.today.sprayTime,
            ),
          }
          : undefined,
      },
    };
  });
};

const buildLatestSprayApplicationByDay = (cropField: SprayApplicationHistoryCropField): Map<number, Date> => {
  const latestSprayByDay = new Map<number, Date>();

  cropField.sprayApplications.forEach((sprayApplication) => {
    const dayKey = getUtcDateKey(sprayApplication.applicationDate);
    const currentLatest = latestSprayByDay.get(dayKey);
    if (!currentLatest || sprayApplication.applicationDate > currentLatest) {
      latestSprayByDay.set(dayKey, sprayApplication.applicationDate);
    }
  });

  return latestSprayByDay;
};

const getMostRecentDate = (firstDate?: Date, secondDate?: Date): Date | undefined => {
  if (!firstDate) {
    return secondDate;
  }

  if (!secondDate) {
    return firstDate;
  }

  return firstDate > secondDate ? firstDate : secondDate;
};

const getUtcDateKey = (date: Date): number => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
